/**
 * Гальмо на відправку OTP — по НОМЕРУ, а не по вкладці (REFACTOR_PLAN п.17).
 *
 * Firebase має власні ліміти на номер і на IP, але дізнаєшся про них уже
 * постфактум: відмовою `auth/too-many-requests`, після якої номер закритий на
 * години, а людина бачить лише «Забагато спроб» і йде. Тому впиратися треба у
 * ВЛАСНУ стелю раніше, ніж у їхню.
 *
 * Стан живе в localStorage, бо кулдаун у пам'яті компонента обходився
 * перезавантаженням сторінки, а сама відправка SMS — ні.
 *
 * Модуль розділений навмисно. Чисте ядро (`pruneOtpLog`, `checkOtpAllowance`,
 * `recordOtpSend`) працює над звичайним об'єктом і приймає `now` параметром —
 * тестується в node без DOM. Обгортки читають і пишуть сховище і мовчки
 * деградують там, де воно недоступне (приватний режим, вимкнені кукі,
 * переповнення): гальмо не має права зламати вхід.
 */

/** Пауза між двома відправками на один номер. */
export const OTP_COOLDOWN_SEC = 60;

/** Стеля відправок на один номер у межах вікна. */
export const OTP_MAX_PER_WINDOW = 5;

/** Ширина вікна для стелі. */
export const OTP_WINDOW_SEC = 60 * 60;

/** Ключ у localStorage; схема — docs/LOCALSTORAGE_SCHEMA.md. */
export const OTP_THROTTLE_LS_KEY = "otp:throttle:v1";

/** Номер у E.164 → мітки часу вдалих відправок (мс). */
export type OtpSendLog = Record<string, number[]>;

export type OtpAllowance =
  | { allowed: true; retryAfterSec: 0 }
  | { allowed: false; reason: "cooldown" | "window"; retryAfterSec: number };

const ALLOWED: OtpAllowance = { allowed: true, retryAfterSec: 0 };

/**
 * Лишає тільки придатні мітки: числа, не старші за вікно і не з майбутнього.
 *
 * Майбутні мітки — не теорія: досить перевести годинник назад, і запис
 * «відправлено» опиниться попереду `now`, заморозивши гальмо назавжди.
 */
function sanitizeStamps(value: unknown, now: number): number[] {
  if (!Array.isArray(value)) return [];
  const cutoff = now - OTP_WINDOW_SEC * 1000;
  return value
    .filter((t): t is number => typeof t === "number" && Number.isFinite(t))
    .filter((t) => t > cutoff && t <= now)
    .sort((a, b) => a - b);
}

/** Прибирає протухлі мітки і номери, від яких нічого не лишилось. */
export function pruneOtpLog(log: OtpSendLog, now: number): OtpSendLog {
  const out: OtpSendLog = {};
  for (const [phone, stamps] of Object.entries(log || {})) {
    const kept = sanitizeStamps(stamps, now);
    if (kept.length) out[phone] = kept;
  }
  return out;
}

/** Чи можна відправляти на цей номер просто зараз. */
export function checkOtpAllowance(
  log: OtpSendLog,
  phoneE164: string,
  now: number,
): OtpAllowance {
  const stamps = sanitizeStamps(log?.[phoneE164], now);
  if (!stamps.length) return ALLOWED;

  const sinceLast = now - stamps[stamps.length - 1];
  if (sinceLast < OTP_COOLDOWN_SEC * 1000) {
    return {
      allowed: false,
      reason: "cooldown",
      retryAfterSec: Math.max(
        1,
        Math.ceil((OTP_COOLDOWN_SEC * 1000 - sinceLast) / 1000),
      ),
    };
  }

  if (stamps.length >= OTP_MAX_PER_WINDOW) {
    // Звільниться тоді, коли найстаріша мітка випаде з вікна.
    return {
      allowed: false,
      reason: "window",
      retryAfterSec: Math.max(
        1,
        Math.ceil((stamps[0] + OTP_WINDOW_SEC * 1000 - now) / 1000),
      ),
    };
  }

  return ALLOWED;
}

/** Нова версія журналу з доданою відправкою (старе чистить принагідно). */
export function recordOtpSend(
  log: OtpSendLog,
  phoneE164: string,
  now: number,
): OtpSendLog {
  const next = pruneOtpLog(log, now);
  next[phoneE164] = [...(next[phoneE164] || []), now];
  return next;
}

// Імена параметрів із підкресленням — тут вони суто документація, і без
// префікса їх ловить no-unused-vars (та сама домовленість, що в stores/auth.ts).
type LocalStorageLike = {
  getItem(_key: string): string | null;
  setItem(_key: string, _value: string): void;
  removeItem(_key: string): void;
};

/** Доступ до сховища, якого може не бути (SSR) або яке може кидати. */
function storage(): LocalStorageLike | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function readOtpLog(now: number = Date.now()): OtpSendLog {
  const ls = storage();
  if (!ls) return {};
  try {
    const raw = ls.getItem(OTP_THROTTLE_LS_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return pruneOtpLog(parsed as OtpSendLog, now);
  } catch {
    // Зіпсований JSON не має замикати вхід — вважаємо, що журнал порожній.
    return {};
  }
}

export function writeOtpLog(log: OtpSendLog): void {
  const ls = storage();
  if (!ls) return;
  try {
    if (!Object.keys(log).length) ls.removeItem(OTP_THROTTLE_LS_KEY);
    else ls.setItem(OTP_THROTTLE_LS_KEY, JSON.stringify(log));
  } catch {
    /* приватний режим або переповнення — мовчки живемо без гальма */
  }
}

/** Перевірка поверх сховища. Порожній номер не гальмуємо — його відсіє форма. */
export function allowOtpSend(
  phoneE164: string,
  now: number = Date.now(),
): OtpAllowance {
  if (!phoneE164) return ALLOWED;
  return checkOtpAllowance(readOtpLog(now), phoneE164, now);
}

/** Зафіксувати ВДАЛУ відправку. Провали не рахуємо — SMS не пішла. */
export function markOtpSent(phoneE164: string, now: number = Date.now()): void {
  if (!phoneE164) return;
  writeOtpLog(recordOtpSend(readOtpLog(now), phoneE164, now));
}
